import type { Address } from 'viem'
import { getRobinhoodPublicClient } from '@/lib/chain/publicClient'
import { curveFactoryAbi, curveLaunchAbi } from '@/lib/launchpad/abi'
import { CURVE_SUPPLY, PHANTOM_QUOTE_WEI } from '@/lib/launchpad/constants'
import { isEthAddress, isNonZeroAddress, isOnChainCurveEnabled, listCurveFactoryAddresses } from '@/lib/launchpad/contracts'
import { chartPointsForLaunch, createLaunchState, graduate } from '@/lib/launchpad/curveMath'
import { listSimLaunches, upsertOnChainLaunch } from '@/lib/launchpad/store'
import { readLiveCreatorFees, totalLiveCreatorFees } from '@/lib/launchpad/tx'
import type { LaunchState } from '@/lib/launchpad/types'
import { isHiddenLaunch, isHiddenListingAddress } from '@/lib/hiddenListings'
import { shouldListOnChainItem } from '@/lib/liveReset'
import { queueLaunchVerification } from '@/lib/verifyLaunch'

const MAX_LAUNCHES = 40
const HYDRATE_BATCH = 6
let launchesHydrateInflight: Promise<void> | null = null
const launchHydrateEpoch = new Map<string, number>()
const launchHydrateInflightByAddress = new Map<string, Promise<LaunchState | undefined>>()

function beginLaunchHydrate(address: string): number {
  const key = address.toLowerCase()
  const epoch = (launchHydrateEpoch.get(key) ?? 0) + 1
  launchHydrateEpoch.set(key, epoch)
  return epoch
}

function isLaunchHydrateCurrent(address: string, epoch: number): boolean {
  return launchHydrateEpoch.get(address.toLowerCase()) === epoch
}

async function readIndexedAddresses(
  client: ReturnType<typeof getRobinhoodPublicClient>,
  factory: Address,
): Promise<Address[]> {
  const length = await client.readContract({
    address: factory,
    abi: curveFactoryAbi,
    functionName: 'allLaunchesLength',
  })
  if (length <= 0n)
    return []

  const count = Number(length > BigInt(MAX_LAUNCHES) ? BigInt(MAX_LAUNCHES) : length)
  const start = length - BigInt(count)
  const indexes = Array.from({ length: count }, (_, i) => start + BigInt(i))
  const addresses = await Promise.all(indexes.map(index => client.readContract({
    address: factory,
    abi: curveFactoryAbi,
    functionName: 'allLaunches',
    args: [index],
  })))
  return [...addresses].reverse()
}

function existingLaunch(address: string): LaunchState | undefined {
  const key = address.toLowerCase()
  return listSimLaunches().find((item) => {
    return item.id.toLowerCase() === key || item.contractAddress?.toLowerCase() === key
  })
}

async function mapLaunch(
  client: ReturnType<typeof getRobinhoodPublicClient>,
  address: Address,
  factoryGraduationQuote?: bigint,
  options?: { includeLiveFees?: boolean },
): Promise<LaunchState | undefined> {
  const existing = existingLaunch(address)
  const [
    name,
    symbol,
    imageUrl,
    creator,
    virtualQuote,
    virtualToken,
    realQuoteRaised,
    tokensSold,
    creatorFeesAccrued,
    platformFeesAccrued,
    status,
    createdAt,
    token,
    graduationQuote,
    phantomQuote,
  ] = await Promise.all([
    client.readContract({ address, abi: curveLaunchAbi, functionName: 'name' }).catch(() => undefined),
    client.readContract({ address, abi: curveLaunchAbi, functionName: 'symbol' }).catch(() => undefined),
    client.readContract({ address, abi: curveLaunchAbi, functionName: 'imageUrl' }).catch(() => undefined),
    client.readContract({ address, abi: curveLaunchAbi, functionName: 'creator' }).catch(() => undefined),
    client.readContract({ address, abi: curveLaunchAbi, functionName: 'virtualQuote' }).catch(() => undefined),
    client.readContract({ address, abi: curveLaunchAbi, functionName: 'virtualToken' }).catch(() => undefined),
    client.readContract({ address, abi: curveLaunchAbi, functionName: 'realQuoteRaised' }).catch(() => undefined),
    client.readContract({ address, abi: curveLaunchAbi, functionName: 'tokensSold' }).catch(() => undefined),
    client.readContract({ address, abi: curveLaunchAbi, functionName: 'creatorFeesAccrued' }).catch(() => undefined),
    client.readContract({ address, abi: curveLaunchAbi, functionName: 'platformFeesAccrued' }).catch(() => undefined),
    client.readContract({ address, abi: curveLaunchAbi, functionName: 'status' }).catch(() => undefined),
    client.readContract({ address, abi: curveLaunchAbi, functionName: 'createdAt' }).catch(() => undefined),
    client.readContract({ address, abi: curveLaunchAbi, functionName: 'token' }).catch(() => undefined),
    client.readContract({ address, abi: curveLaunchAbi, functionName: 'graduationQuote' }).catch(() => undefined),
    client.readContract({ address, abi: curveLaunchAbi, functionName: 'phantomQuote' }).catch(() => undefined),
  ])

  const resolvedToken = isNonZeroAddress(token)
    ? token
    : (isNonZeroAddress(existing?.tokenAddress) ? existing.tokenAddress : undefined)

  const resolvedName = (name || existing?.name || '').trim()
  const resolvedTicker = (symbol || existing?.ticker || '').trim().toUpperCase()
  const resolvedCreator = creator || existing?.creatorAddress
  if (!resolvedName || !resolvedTicker || !resolvedCreator)
    return undefined

  const createdAtMs = createdAt && createdAt > 0n
    ? Number(createdAt) * 1000
    : (existing?.createdAtMs ?? 0)

  const resolvedGraduation = (graduationQuote && graduationQuote > 0n)
    ? graduationQuote
    : ((factoryGraduationQuote && factoryGraduationQuote > 0n)
        ? factoryGraduationQuote
        : existing?.graduationQuote)

  const base = createLaunchState({
    id: existing?.id ?? address.toLowerCase(),
    name: resolvedName,
    ticker: resolvedTicker,
    imageUrl: (typeof imageUrl === 'string' && imageUrl.trim()) ? imageUrl.trim() : (existing?.imageUrl ?? ''),
    socials: existing?.socials,
    creatorAddress: resolvedCreator,
    createdAtMs,
    contractAddress: address,
    tokenAddress: resolvedToken,
    shareSlug: existing?.shareSlug,
    graduationQuote: resolvedGraduation,
  })

  // Live curve numbers must come from this RPC pass only. Falling back to
  // this browser's localStorage snapshot is what made raised ETH and charts
  // differ across devices.
  if (virtualQuote === undefined || virtualToken === undefined)
    return undefined

  const resolvedVirtualQuote = virtualQuote < 0n ? 0n : virtualQuote
  const resolvedVirtualToken = virtualToken < 0n ? 0n : virtualToken
  const resolvedPhantomQuote = (phantomQuote && phantomQuote > 0n) ? phantomQuote : PHANTOM_QUOTE_WEI
  const chainGraduated = status === 1

  const derivedRealQuoteRaised = resolvedVirtualQuote > resolvedPhantomQuote
    ? resolvedVirtualQuote - resolvedPhantomQuote
    : 0n
  const chainRealQuoteRaised = realQuoteRaised !== undefined && realQuoteRaised > 0n
    ? realQuoteRaised
    : 0n
  const resolvedRealQuoteRaised = chainGraduated
    ? (chainRealQuoteRaised > 0n ? chainRealQuoteRaised : derivedRealQuoteRaised)
    : derivedRealQuoteRaised

  const soldFromReserves = base.virtualToken > resolvedVirtualToken
    ? base.virtualToken - resolvedVirtualToken
    : 0n
  const resolvedTokensSold = soldFromReserves > 0n
    ? (soldFromReserves > CURVE_SUPPLY ? CURVE_SUPPLY : soldFromReserves)
    : (tokensSold !== undefined
        ? (tokensSold < 0n ? 0n : (tokensSold > CURVE_SUPPLY ? CURVE_SUPPLY : tokensSold))
        : 0n)

  // A trade happened since the last hydrate if sold tokens or raised quote moved;
  // only then do we bump lastTradeAtMs to now. Otherwise preserve the last known
  // trade time (or fall back to createdAtMs for a launch with no trades yet) so
  // "Recently Bought" never regresses to stale creation-time timestamps.
  const traded = (existing && (existing.tokensSold !== resolvedTokensSold || existing.realQuoteRaised !== resolvedRealQuoteRaised))
    || (!existing && resolvedTokensSold > 0n)
  const resolvedLastTradeAtMs = traded ? Date.now() : (existing?.lastTradeAtMs ?? createdAtMs)

  const mapped: LaunchState = {
    ...base,
    virtualQuote: resolvedVirtualQuote,
    virtualToken: resolvedVirtualToken,
    k: resolvedVirtualQuote * resolvedVirtualToken,
    realQuoteRaised: resolvedRealQuoteRaised,
    graduationQuote: resolvedGraduation && resolvedGraduation > 0n ? resolvedGraduation : base.graduationQuote,
    tokensSold: resolvedTokensSold,
    lastTradeAtMs: resolvedLastTradeAtMs,
    tokenAddress: resolvedToken ?? address,
    creatorFeesAccrued: creatorFeesAccrued ?? 0n,
    platformFeesAccrued: platformFeesAccrued ?? 0n,
    antiSnipeFeesAccrued: 0n,
    status: 'curve',
    priceHistory: base.priceHistory,
  }
  const graduated = chainGraduated
  const next = graduated
    ? (() => {
        try {
          return graduate({ ...mapped, status: 'curve' })
        }
        catch {
          return { ...mapped, status: 'graduated' as const }
        }
      })()
    : { ...mapped, status: 'curve' as const }

  if (!options?.includeLiveFees) {
    return {
      ...next,
      priceHistory: chartPointsForLaunch(next),
    }
  }

  const liveFees = await readLiveCreatorFees(next, client).catch(() => undefined)
  const tokenAddress = liveFees?.token ?? next.tokenAddress
  const creatorFeesAccruedLive = liveFees
    ? totalLiveCreatorFees(liveFees)
    : next.creatorFeesAccrued

  return {
    ...next,
    tokenAddress,
    creatorFeesAccrued: creatorFeesAccruedLive,
    priceHistory: chartPointsForLaunch({ ...next, tokenAddress, creatorFeesAccrued: creatorFeesAccruedLive }),
  }
}

async function hydrateAndStoreLaunch(
  client: ReturnType<typeof getRobinhoodPublicClient>,
  address: Address,
  factoryGraduationQuote?: bigint,
  options?: { includeLiveFees?: boolean, verify?: boolean },
): Promise<LaunchState | undefined> {
  const epoch = beginLaunchHydrate(address)
  const mapped = await mapLaunch(client, address, factoryGraduationQuote, options)
  if (!isLaunchHydrateCurrent(address, epoch))
    return existingLaunch(address)
  const existing = existingLaunch(address)
  if (mapped && !isHiddenLaunch(mapped) && shouldListOnChainItem(mapped.createdAtMs, Boolean(existing))) {
    upsertOnChainLaunch(mapped)
    const distinctToken = mapped.tokenAddress
      && mapped.tokenAddress.toLowerCase() !== address.toLowerCase()
      ? mapped.tokenAddress
      : undefined
    const justGraduated = mapped.status === 'graduated' && existing?.status !== 'graduated'
    if (options?.verify || justGraduated || (mapped.status === 'graduated' && distinctToken))
      queueLaunchVerification({ kind: 'curve', address, token: distinctToken })
    return mapped
  }
  return undefined
}

export async function hydrateOnChainLaunches() {
  if (typeof window === 'undefined' || !isOnChainCurveEnabled())
    return
  if (launchesHydrateInflight)
    return launchesHydrateInflight

  launchesHydrateInflight = (async () => {
    const factories = listCurveFactoryAddresses()
    if (!factories.length)
      return

    const client = getRobinhoodPublicClient()
    const seen = new Set<string>()
    for (const factory of factories) {
      let addresses: Address[] = []
      try {
        addresses = await readIndexedAddresses(client, factory)
      }
      catch {
        continue
      }

      const factoryGraduationQuote = await client.readContract({
        address: factory,
        abi: curveFactoryAbi,
        functionName: 'graduationQuote',
      }).catch(() => undefined)

      const eligible = addresses.filter((address) => {
        if (!isEthAddress(address) || isHiddenListingAddress(address))
          return false
        const key = address.toLowerCase()
        if (seen.has(key))
          return false
        seen.add(key)
        return true
      })
      for (let i = 0; i < eligible.length; i += HYDRATE_BATCH) {
        const batch = eligible.slice(i, i + HYDRATE_BATCH)
        await Promise.all(batch.map(async (address) => {
          try {
            await hydrateAndStoreLaunch(client, address, factoryGraduationQuote, { includeLiveFees: true })
          }
          catch {
            // Skip launches whose ABI surface does not match this client.
          }
        }))
      }
    }
  })().finally(() => {
    launchesHydrateInflight = null
  })

  return launchesHydrateInflight
}

export async function hydrateOnChainLaunch(
  address: Address,
  options?: { verify?: boolean, force?: boolean },
): Promise<LaunchState | undefined> {
  if (typeof window === 'undefined' || !isOnChainCurveEnabled() || !isEthAddress(address) || isHiddenListingAddress(address))
    return undefined
  const key = address.toLowerCase()
  const inflight = launchHydrateInflightByAddress.get(key)
  if (inflight && options?.force !== true)
    return inflight

  const work = (async () => {
    const client = getRobinhoodPublicClient()
    try {
      // Launch.graduationQuote() is already read in mapLaunch; skip the extra factory RPC.
      // force=true still bumps the hydrate epoch so a page refresh or post-trade
      // read wins over an older in-flight listing hydrate.
      return await hydrateAndStoreLaunch(client, address, existingLaunch(address)?.graduationQuote, {
        verify: options?.verify === true,
      })
    }
    catch {
      return undefined
    }
  })().finally(() => {
    if (launchHydrateInflightByAddress.get(key) === work)
      launchHydrateInflightByAddress.delete(key)
  })

  launchHydrateInflightByAddress.set(key, work)
  return work
}
